'use client';

import Link from 'next/link';
import { Poppins } from 'next/font/google';
import { BRAND_BLUE, BRAND_GREEN, BRAND_YELLOW } from '@voley/shared-types/constants';

const poppins = Poppins({ subsets: ['latin'], weight: ['700'] });

export default function BlogPage() {
  // Brand color to align with landing/pricing
  const primaryColor = BRAND_BLUE;
  
  return (
    <div className="bg-white">
      {/* Hero Section */}
      <div className="bg-white py-12 sm:py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1
              className={`text-5xl sm:text-6xl md:text-7xl lg:text-8xl xl:text-9xl ${poppins.className} tracking-tight leading-tight`}
            >
              <span className="block font-medium text-gray-900">
                Voley journal
              </span>
              <span className="block font-medium text-gray-900">
                ideas for modern agencies.
              </span>
            </h1>
            <p className="mt-4 sm:mt-6 max-w-3xl mx-auto text-base sm:text-lg md:text-xl lg:text-2xl font-light text-gray-600 px-2">
              Deep dives, practical playbooks, and stories about how insurance teams are modernizing
              communication.
            </p>
            <div className="mt-6 flex items-center justify-center gap-3">
              <span
                className="h-2.5 w-2.5 rounded-full"
                style={{ backgroundColor: primaryColor }}
              />
              <span
                className="h-2.5 w-2.5 rounded-full"
                style={{ backgroundColor: BRAND_GREEN }}
              />
              <span
                className="h-2.5 w-2.5 rounded-full"
                style={{ backgroundColor: BRAND_YELLOW }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Coming Soon Message */}
      <div className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div 
            className="inline-flex items-center rounded-full px-4 py-1.5 text-xs font-semibold tracking-wide uppercase mb-6"
            style={{ backgroundColor: `${primaryColor}10`, color: primaryColor }}
          >
            Blog coming soon
          </div>
          <h2 className="text-3xl sm:text-4xl font-semibold text-gray-900 mb-4">
            Content that helps your agency move faster.
          </h2>
          <p className="text-lg sm:text-xl text-gray-600 mb-4">
            We&apos;re working on guides, stories, and frameworks for client communication, compliance,
            and team collaboration.
          </p>
          <p className="text-base text-gray-500 mb-8">
            Until then, you can connect with our team for live walkthroughs and early product updates.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link
              href="/request-demo"
              className="inline-flex items-center justify-center px-6 py-3 border-0 text-base font-medium rounded-full text-white shadow-sm hover:shadow-lg transition-all hover:scale-[1.01]"
              style={{ backgroundColor: primaryColor }}
            >
              Schedule a walkthrough
            </Link>
            <Link
              href="/contact"
              className="inline-flex items-center justify-center px-6 py-3 border border-gray-200 text-base font-medium rounded-full text-gray-700 bg-white hover:bg-gray-50 hover:border-gray-300 transition-all"
            >
              Contact our team
            </Link>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-gradient-to-br from-brand/75 via-brand-green/75 to-brand-yellow/75">
        <div className="max-w-7xl mx-auto py-10 sm:py-12 px-4 sm:px-6 lg:py-16 lg:px-8 lg:flex lg:items-center lg:justify-between">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-semibold tracking-tight text-white">
            <span className="block">Ready to modernize your client communication?</span>
            <span className="block opacity-90 font-medium">See Voley in action with your own workflows.</span>
          </h2>
          <div className="mt-6 sm:mt-8 flex lg:mt-0 lg:shrink-0">
            <div className="inline-flex w-full sm:w-auto">
              <Link
                href="/request-demo"
                className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-full text-gray-900 bg-white hover:bg-slate-50 transition-all hover:scale-[1.02] hover:shadow-lg w-full sm:w-auto"
              >
                Book a demo
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
